// usePermissions — Settings-03 / Settings-03b.
//
// Central permission hook for the launch model. Reads the active
// membership from ``useAuth`` and exposes the booleans nav, settings
// tabs, and route guards check. The backend ships a resolved
// ``permissions`` summary on every /me membership; the hook prefers
// that and only falls back to FE-side derivation when an older
// /me payload (or a test scaffold) omits it.
//
// Settings-03b — memberships hold a *set* of product roles
// (``owner`` / ``admin`` / ``member`` / ``sales``). A single user
// can hold multiple at once: an Account Manager who also helps
// sales has ``["member", "sales"]``. Permission helpers ask
// whether *any* role in the set unlocks the surface.
//
// ``is_producer`` is a business flag for producer reporting only —
// it does NOT grant any product-surface access. Sales CRM access
// is explicit: ``owner`` or ``sales`` only (admin alone does not
// grant Sales CRM access).

import { useContext, useMemo } from "react";

import { AuthContext } from "./AuthContext";
import type {
  AuthMembership, AuthPermissions, BrokerProductRole,
} from "./types";


export interface PermissionFlags extends AuthPermissions {
  // The product role-set the active membership holds. Useful for
  // UI surfaces that want to render a label or branch on the
  // conceptual role list, not the resolved booleans.
  roles: BrokerProductRole[];
  isProducer: boolean;
}


export const EMPTY_PERMISSIONS: PermissionFlags = {
  is_owner: false,
  is_admin_or_owner: false,
  can_manage_team: false,
  can_manage_locations: false,
  can_manage_account_team: false,
  can_manage_billing: false,
  can_manage_broker_settings: false,
  can_access_sales_crm: false,
  roles: [],
  isProducer: false,
};


// Generic role-set predicates the rest of the FE imports when it
// needs a role check that doesn't fit one of the named permission
// gates (e.g. branching a label, conditionally enabling a button
// for owners only).
export function hasRole(
  roles: readonly string[] | undefined | null, role: BrokerProductRole,
): boolean {
  return Array.isArray(roles) && roles.includes(role);
}


export function hasAnyRole(
  roles: readonly string[] | undefined | null,
  candidates: readonly BrokerProductRole[],
): boolean {
  if (!Array.isArray(roles)) return false;
  for (const c of candidates) {
    if (roles.includes(c)) return true;
  }
  return false;
}


// Fall-through derivation when /me did not carry a ``permissions``
// summary (e.g. server still on the older shape, or a unit test
// stubs a membership with just ``roles``). Mirrors the backend
// ``permissions_for_membership`` so both sides answer the same.
function derivePermissions(membership: AuthMembership): AuthPermissions {
  const roles = membership.roles ?? [];
  const isOwner = hasRole(roles, "owner");
  const isAdminOrOwner = hasAnyRole(roles, ["owner", "admin"]);
  return {
    is_owner: isOwner,
    is_admin_or_owner: isAdminOrOwner,
    can_manage_team: isAdminOrOwner,
    can_manage_locations: isAdminOrOwner,
    can_manage_account_team: isAdminOrOwner,
    can_manage_billing: isOwner,
    can_manage_broker_settings: isOwner,
    can_access_sales_crm: hasAnyRole(roles, ["owner", "sales"]),
  };
}


function normalizeRoles(
  roles: readonly string[] | undefined | null,
): BrokerProductRole[] {
  if (!Array.isArray(roles)) return [];
  const valid = new Set<BrokerProductRole>(
    ["owner", "admin", "member", "sales"],
  );
  const out: BrokerProductRole[] = [];
  for (const r of roles) {
    if (typeof r === "string" && valid.has(r as BrokerProductRole)) {
      out.push(r as BrokerProductRole);
    }
  }
  return out;
}


export function permissionsFromMembership(
  membership: AuthMembership | null | undefined,
): PermissionFlags {
  if (membership == null) return EMPTY_PERMISSIONS;
  const summary = membership.permissions ?? derivePermissions(membership);
  return {
    ...summary,
    roles: normalizeRoles(membership.roles),
    isProducer: Boolean(membership.is_producer),
  };
}


export function usePermissions(): PermissionFlags {
  // Read the AuthContext directly rather than going through
  // ``useAuth()``: that hook throws when called outside the
  // provider, but for permissions the right behaviour is to fall
  // back to the empty (all-gates-closed) flag set. Production paths
  // sit behind ``ProtectedRoute`` so this only matters in tests +
  // future surfaces that may render the layout pre-auth.
  const ctx = useContext(AuthContext);
  const currentMembership = ctx?.currentMembership ?? null;
  return useMemo(
    () => permissionsFromMembership(currentMembership),
    [currentMembership],
  );
}
